import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { permissionService } from '../services/permission';
import { useAuthStore } from './authStore';

interface PermissionState {
  permissions: string[];
  menuPermissions: string[];
  isLoading: boolean;
  loadPermissions: () => Promise<void>;
  loadMenuPermissions: () => Promise<void>;
  setPermissions: (permissions: string[]) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  clearPermissions: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  persist(
    (set, get) => ({
      permissions: [],
      menuPermissions: [],
      isLoading: false,

  loadPermissions: async () => {
    // 从认证store获取token
    const token = useAuthStore.getState().token;
    if (!token) {
      console.log('❌ 未找到认证token，跳过权限加载');
      return;
    }

    console.log('✅ 找到认证token，开始加载用户权限...');
    set({ isLoading: true });
    
    try {
      const userPermissions = await permissionService.getUserPermissions();
      console.log('✅ 用户权限加载成功:', userPermissions.permissions);
      set({ permissions: userPermissions.permissions });
    } catch (error: any) {
      console.error('❌ 加载权限失败:', error);
      // 如果是401错误，不设置空权限，让认证系统处理
      if (error.response?.status !== 401) {
        // 权限加载失败时，设置默认权限以确保菜单显示
        console.log('⚠️ 权限加载失败，设置默认权限');
        set({ permissions: ['*:*'] }); // 设置超级权限作为备用
      }
    } finally {
      set({ isLoading: false });
    }
  },

  loadMenuPermissions: async () => {
    // 从认证store获取token
    const token = useAuthStore.getState().token;
    if (!token) {
      console.log('❌ 未找到认证token，跳过菜单权限加载');
      return;
    }

    console.log('✅ 找到认证token，开始加载菜单权限...');
    
    try {
      const menuPermissions = await permissionService.getMenuPermissions();
      console.log('✅ 菜单权限加载成功:', menuPermissions.menu_permissions);
      set({ menuPermissions: menuPermissions.menu_permissions });
    } catch (error: any) {
      console.error('❌ 加载菜单权限失败:', error);
      // 如果是401错误，不设置空权限，让认证系统处理
      if (error.response?.status !== 401) {
        // 不设置空权限，保持原有权限或默认允许访问
        console.log('⚠️ 菜单权限加载失败，保持原有权限状态');
      }
    }
  },

  setPermissions: (permissions: string[]) => {
    console.log('🔧 直接设置权限数据:', permissions);
    set({ permissions });
  },

  hasPermission: (permission: string) => {
    const { permissions, isLoading } = get();
    console.log(`🔍 检查权限: ${permission}`, { permissions, isLoading });
    
    // 如果正在加载或权限数组不存在，返回false
    if (isLoading || !permissions || !Array.isArray(permissions)) {
      console.log(`❌ 权限检查失败: 加载中=${isLoading}, 权限数组=${permissions}`);
      return false;
    }
    
    const hasPermission = permissions.includes('*:*') || permissions.includes(permission);
    console.log(`✅ 权限检查结果: ${permission} = ${hasPermission}`);
    return hasPermission;
  },

  hasAnyPermission: (permissionList: string[]) => {
    const { permissions, isLoading } = get();
    console.log(`🔍 检查任意权限:`, permissionList, { permissions, isLoading });
    
    // 如果正在加载或权限数组不存在，返回false
    if (isLoading || !permissions || !Array.isArray(permissions)) {
      console.log(`❌ 任意权限检查失败: 加载中=${isLoading}, 权限数组=${permissions}`);
      return false;
    }
    
    const hasPermission = permissionList.some(permission => 
      permissions.includes('*:*') || permissions.includes(permission)
    );
    console.log(`✅ 任意权限检查结果:`, permissionList, `= ${hasPermission}`);
    return hasPermission;
  },

  hasAllPermissions: (permissionList: string[]) => {
    const { permissions, isLoading } = get();
    console.log(`🔍 检查所有权限:`, permissionList, { permissions, isLoading });
    
    // 如果正在加载或权限数组不存在，返回false
    if (isLoading || !permissions || !Array.isArray(permissions)) {
      console.log(`❌ 所有权限检查失败: 加载中=${isLoading}, 权限数组=${permissions}`);
      return false;
    }
    
    const hasPermission = permissionList.every(permission => 
      permissions.includes('*:*') || permissions.includes(permission)
    );
    console.log(`✅ 所有权限检查结果:`, permissionList, `= ${hasPermission}`);
    return hasPermission;
  },

  clearPermissions: () => {
    console.log('🧹 清除权限数据');
    set({ permissions: [], menuPermissions: [] });
  },
    }),
    {
      name: 'permission-storage',
      partialize: (state) => ({
        permissions: state.permissions,
        menuPermissions: state.menuPermissions,
      }),
    }
  )
);